import React, { useEffect, useState } from "react";
import { browserHistory } from "react-router";
import { Link } from "react-router";
import PropTypes from "prop-types";
import _ from "lodash";
import moment from "moment";
import util from "../util/util";
import LoadingIndicatorText from "../loading-indicator/loading-indicator-text";
import Pagination from "../pagination/pagination";

const jQuery = window.jQuery;

export default function ChangeLogByObj(props) {
  // id used to go get the object data;
  const [id, setId] = useState(0);
  // change log to display (will be populated by an Ajax call);
  const [log, setLog] = useState([]);
  // the type of object to view (rule, pod, campaign);
  const [objType, setObjType] = useState(null);
  // the object name
  const [objName, setObjName] = useState(``);
  // the object url
  const [objUrl, setObjUrl] = useState(``);
  // page of object results;
  const [page, setPage] = useState(1);
  // number of pages to display in the pagination links;
  const [pageCount, setPageCount] = useState(0);
  // pass to sort and pagination links to maintain search state;
  const [queryString, setQueryString] = useState(null);
  // list sort value;
  const [sort, setSort] = useState(`-date_created`);

  useEffect(() => {
    const objectId = Number(_.get(props, `params.id`, 0));
    const objectType = _.get(props, `params.obj`, null);
    const pageNo = Number(_.get(props, `params.page`, 1));
    const sortBy = _.get(props, `location.query.sort`, `-date_created`);

    const getChangeLog = () => {
      // if this is an exception, stop right here;
      // need to convert the exception id into the rule id;
      if (objectType === `exception`) {
        return util.getException(objectId).then(data => {
          browserHistory.replace(
            util.returnChangeLogDetailUrl(`rules.rule`, data.rule)
          );
        });
      }

      let getObj = null;
      let returnObjUrl = null;

      // determine what the functions will be (diff based on object type);
      if (objectType === `rule`) {
        getObj = util.getRule;
        returnObjUrl = util.returnRuleDetailUrl;
      } else if (objectType === `campaign`) {
        getObj = util.getCampaign;
        returnObjUrl = util.returnCampaignDetailUrl;
      } else if (objectType === `pod`) {
        getObj = util.getPod;
        returnObjUrl = util.returnPodDetailUrl;
      }

      // not sure what the scenario would be where a user gets to this page
      // and it's not one of the above types;
      // but in case the obj isn't found, don't throw an error;
      if (getObj) {
        // now we know what type of object it is;
        // get the object name, and set the `back to detail page` url;
        getObj(objectId).then(data => {
          setTimeout(() => {
            setObjName(data.name);
            setObjUrl(returnObjUrl(objectId));
          }, util.delayList);
        });
      } else {
        setObjName(`Unknown Object`);
      }

      // get the change log content;
      util
        .getChangeLog({
          contentType: util.returnChangeLogPath(objectType).key,
          id: objectId,
          page: pageNo,
          sort: sortBy
        })
        .then(data => {
          setTimeout(() => {
            setId(objectId);
            setObjType(objectType);
            setLog(data.results.length ? data.results : []);
            setPage(pageNo);
            setPageCount(Math.ceil(data.count / util.changeLogResultsPerPage));
            setQueryString(_.get(props, `location.search`, null));
            setSort(sortBy);
          }, util.delayList);
        });
    };

    getChangeLog();
  }, [props]);

  // three options for display (loading, no results, results);
  const renderRows = (logs = false) => {
    if (logs.length === 0) {
      return (
        <tr>
          <td colSpan="4">
            <LoadingIndicatorText />
          </td>
        </tr>
      );
    } else if (logs === false) {
      return (
        <tr>
          <td>
            <strong>0</strong>
          </td>
          <td colSpan="3">{util.zeroLogsMessage}</td>
        </tr>
      );
    } else {
      return logs.map((log, index) => {
        return index < util.changeLogResultsPerPage ? (
          <tr key={index}>
            <td>
              <strong>
                {util.returnLineNumber(
                  page,
                  util.changeLogResultsPerPage,
                  index
                )}
              </strong>
            </td>
            <td>
              <Link
                to={util.returnChangeLogDetailUrl(`user`, log.revision.user.id)}
              >
                {log.revision.user.email || log.revision.user.name}
              </Link>
            </td>
            <td>{log.description}</td>
            <td>
              {moment(log.revision.date_created).format(
                util.dateFormatHumanLong
              )}
            </td>
          </tr>
        ) : null;
      });
    }
  };

  // display table header sort links;
  const renderSortLink = (sortValue, sortDisplay) => {
    const className = util.returnSortClass(sortValue, sort);
    const newSortValue = util.returnSortValue(sortValue, sort);
    const queryStringObject = queryString
      ? jQuery.deparam(queryString.replace(`?`, ``))
      : {};

    // add the new sort value to the querystring object;
    queryStringObject.sort = newSortValue;

    return (
      <Link
        to={`${util.returnChangeLogDetailUrl(objType, id)}?${jQuery.param(
          queryStringObject
        )}`}
        className={className}
      >
        {sortDisplay}
        <i className="fa fa-sort-desc" aria-hidden="true"></i>
        <i className="fa fa-sort-asc" aria-hidden="true"></i>
      </Link>
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-sm-6 col-sm-push-6">
          <p className="page__actions">
            {objUrl && objType ? (
              <Link to={objUrl} className="btn btn-default">
                <i className="fa fa-arrow-up" aria-hidden="true"></i>
                Return to {objType.split(`.`).slice(-1)} detail
              </Link>
            ) : null}
          </p>
        </div>
        <div className="col-sm-6 col-sm-pull-6">
          <h4 className="page__title">
            Change Log: {objName ? objName : <LoadingIndicatorText />}
          </h4>
        </div>
      </div>

      <hr />

      <div className="table-responsive">
        <table className="table table-striped table-bordered table-hover">
          <colgroup>
            <col style={{ width: `1%` }}></col>
            <col style={{ width: `40%` }}></col>
            <col style={{ width: `40%` }}></col>
            <col style={{ width: `19%` }}></col>
          </colgroup>
          <thead>
            <tr>
              <th>#</th>
              <th>User</th>
              <th>Change</th>
              <th>{renderSortLink(`-date_created`, `Date`)}</th>
            </tr>
          </thead>
          <tbody>{renderRows(log)}</tbody>
        </table>
      </div>

      <Pagination
        currentPage={page}
        pageCount={pageCount}
        path={`changelog/${objType}/${id}`}
        queryString={queryString}
      />
    </div>
  );
}

ChangeLogByObj.propTypes = {
  location: PropTypes.object,
  params: PropTypes.object
};
